import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { catalogApi, type CategoryNode, type ProductSummary } from "../../api/catalog";
import { discountsApi, type DiscountView } from "../../api/discounts";
import { useToast } from "../Toast";
import { Select } from "../Select";
import Ic from "../icons.jsx";
import type { ApiError } from "../../api/client";

function flatten(nodes: CategoryNode[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((n) => [
    { id: n.id, label: `${"— ".repeat(depth)}${n.name}` },
    ...flatten(n.children ?? [], depth + 1),
  ]);
}

export function DiscountMatrixCard({ companyId }: { companyId: string }) {
  const { canWrite, canFull } = useSectionAccess("discounts");
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const toast = useToast();
  const [rows, setRows] = useState<DiscountView[]>([]);
  const [cats, setCats] = useState<{ id: string; label: string }[]>([]);
  const [level, setLevel] = useState<"CATEGORY" | "PRODUCT">("CATEGORY");
  const [categoryId, setCategoryId] = useState("");
  const [percent, setPercent] = useState("");
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ProductSummary[]>([]);
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => discountsApi.list(companyId).then(setRows).catch(() => setRows([]));

  useEffect(() => {
    void reload();
    // Category/product names in the pickers follow the app language.
    void catalogApi.tree(locale).then((tree) => setCats(flatten(tree))).catch(() => setCats([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, locale]);

  useEffect(() => {
    if (level !== "PRODUCT" || term.trim().length < 2 || picked) {
      setResults([]);
      return;
    }
    let live = true;
    void catalogApi.searchProducts(term.trim(), locale, 8).then((r) => { if (live) setResults(r); }).catch(() => {});
    return () => { live = false; };
  }, [term, level, picked, locale]);

  const add = async () => {
    const pct = Number(percent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error(t("admin.discounts.err.percent", "Percent must be 0–100"));
      return;
    }
    if (level === "CATEGORY" && !categoryId) {
      toast.error(t("admin.discounts.err.pickCategory", "Pick a category"));
      return;
    }
    if (level === "PRODUCT" && !picked) {
      toast.error(t("admin.discounts.err.pickProduct", "Pick a product"));
      return;
    }
    const body = level === "CATEGORY"
      ? { categoryId, percent: pct }
      : { productId: picked!.id, percent: pct };
    setBusy(true);
    try {
      await discountsApi.upsert(companyId, body);
      toast.success(t("admin.discounts.saved", "Discount saved"));
      setPercent(""); setPicked(null); setTerm(""); setCategoryId("");
      await reload();
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("admin.discounts.err.save", "Failed to save"));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id: string) => {
    setBusy(true);
    try {
      await discountsApi.remove(companyId, id);
      await reload();
    } catch (e) {
      toast.error((e as ApiError)?.message ?? t("admin.discounts.err.remove", "Failed to remove"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <header className="card-h">
        <h3>{t("admin.discounts.title", "Customer discounts")}</h3>
        <span className="sub">{t("admin.discounts.subtitle", "A product override beats a category default.")}</span>
      </header>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
          {t("admin.discounts.intro", "Standing discounts auto-pulled onto this customer's orders.")}
        </p>

        {rows.length === 0 ? (
          <div style={emptyState}>
            <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("admin.discounts.empty.title", "No discounts yet.")}</p>
            <p style={{ margin: "6px 0 0", color: "var(--ink-4)", fontSize: 12 }}>
              {t("admin.discounts.empty.hint", "Add a category or product discount below.")}
            </p>
          </div>
        ) : (
          <table className="list-card" style={tbl}>
            <thead>
              <tr>
                <th style={th}>{t("admin.discounts.col.level", "Level")}</th>
                <th style={th}>{t("admin.discounts.col.target", "Target")}</th>
                <th style={{ ...th, textAlign: "right" }}>%</th>
                <th style={{ ...th, width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={td} data-label={t("admin.discounts.col.level", "Level")}>
                    {r.level === "PRODUCT"
                      ? t("admin.discounts.level.product", "Product")
                      : t("admin.discounts.level.category", "Category")}
                  </td>
                  <td style={td} data-label={t("admin.discounts.col.target", "Target")}>{r.targetName ?? r.targetId}</td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }} data-label="%">{r.percent}%</td>
                  <td style={{ ...td, textAlign: "right" }} className="card-actions">
                    <button type="button" className="btn ghost sm" style={{ color: "var(--err)" }} aria-label={t("admin.discounts.remove", "Remove discount")} disabled={busy || !canFull} onClick={() => del(r.id)}>
                      <Ic.x />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", paddingTop: 12, borderTop: "1px solid var(--border)" }}>
          <div style={fld}>
            <span style={fieldLabel}>{t("admin.discounts.col.level", "Level")}</span>
            <Select<"CATEGORY" | "PRODUCT">
              value={level}
              onChange={(v) => { setLevel(v); setPicked(null); setTerm(""); setCategoryId(""); }}
              ariaLabel={t("admin.discounts.levelAria", "Discount level")}
              style={{ minWidth: 150 }}
              options={[
                { value: "CATEGORY", label: t("admin.discounts.level.category", "Category") },
                { value: "PRODUCT", label: t("admin.discounts.level.product", "Product") },
              ]}
            />
          </div>

          {level === "CATEGORY" ? (
            <div style={fld}>
              <span style={fieldLabel}>{t("admin.discounts.level.category", "Category")}</span>
              <Select
                value={categoryId}
                onChange={setCategoryId}
                ariaLabel={t("admin.discounts.level.category", "Category")}
                placeholder={t("admin.discounts.pick", "— pick —")}
                style={{ minWidth: 260 }}
                options={cats.map((c) => ({ value: c.id, label: c.label }))}
              />
            </div>
          ) : (
            <label style={{ ...fld, position: "relative", minWidth: 260 }}>
              <span style={fieldLabel}>{t("admin.discounts.level.product", "Product")}</span>
              <input
                value={picked ? `${picked.sku} — ${picked.name}` : term}
                onChange={(e) => { setPicked(null); setTerm(e.target.value); }}
                placeholder={t("admin.discounts.searchPlaceholder", "Search SKU / name")}
                className="input"
              />
              {!picked && results.length > 0 && (
                <div className="dd-scroll" style={typeaheadPanel}>
                  {results.map((p) => (
                    <button type="button" key={p.id} style={typeaheadOption} onClick={() => { setPicked(p); setResults([]); }}>
                      <span style={mono}>{p.sku}</span>
                      <span style={{ flex: 1 }}>{p.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </label>
          )}

          <label style={fld}>
            <span style={fieldLabel}>{t("admin.discounts.field.percent", "Percent")}</span>
            <input
              type="number" min={0} max={100} step="0.5" value={percent}
              onChange={(e) => setPercent(e.target.value)}
              className="input" style={{ width: 90, textAlign: "right" }}
            />
          </label>
          <button type="button" className="btn primary" disabled={busy} onClick={add}>
            <Ic.check /> {t("action.save", "Save")}
          </button>
        </fieldset>
      </div>
    </section>
  );
}

const tbl: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th: CSSProperties = {
  textAlign: "left", padding: "10px 8px", color: "var(--ink-3)", fontWeight: 600,
  borderBottom: "1px solid var(--border)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
};
const td: CSSProperties = { padding: "10px 8px", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
const fld: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-4)" };
const emptyState: CSSProperties = {
  padding: 20, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
};
const typeaheadPanel: CSSProperties = {
  position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0, right: 0,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)", maxHeight: 240, overflowY: "auto",
};
const typeaheadOption: CSSProperties = {
  display: "flex", gap: 12, alignItems: "center", padding: "8px 10px", width: "100%", textAlign: "left",
  background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "var(--ink)",
  borderBottom: "1px solid var(--border)",
};
